import React, { useState, useContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  TextField,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { SearchOutlined, SendOutlined } from '@material-ui/icons';
import { AppContext } from '../../modules/AppContext';
import FCGrid from '../../components/FCGrid';
import { LoaderSubmitButton } from '../../components/LoaderSubmitButton';
import { integratedTransitionApi } from '../../modules/IntegratedTransition/IntegratedTransition';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
  content: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, 0),
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  fieldGroupLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  validationList: {
    listStyle: 'none',
    padding: 0,
    margin: theme.spacing(2, 0),
  },
  validationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
}));

const STATUS_COLORS = {
  Managed: '#4caf50',
  default: '#f44336',
};

const getStatusColor = (status) =>
  STATUS_COLORS[status] || STATUS_COLORS.default;

export const IntegratedTransition = () => {
  const { showToast } = useContext(AppContext);
  const classes = useStyles();

  const [currentDistributorId, setCurrentDistributorId] = useState('');
  const [currentPlanSponsorId, setCurrentPlanSponsorId] = useState('');
  const [newDistributorId, setNewDistributorId] = useState('');
  const [newPlanSponsorId, setNewPlanSponsorId] = useState('');
  const [sameDistributorId, setSameDistributorId] = useState(false);
  const [samePlanSponsorId, setSamePlanSponsorId] = useState(false);

  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetched, setFetched] = useState(false);

  const validation = useMemo(() => {
    if (participants.length === 0) {
      return {
        valid: false,
        allManaged: false,
        anyMatched: false,
        noDuplicates: false,
      };
    }

    const allManaged = participants.every((p) => p.oldStatus === 'Managed');
    const anyMatched = participants.some(
      (p) => p.selected && p.newParticipantId != null
    );
    const noSelectedUnmatched = !participants.some(
      (p) => p.selected && p.newParticipantId == null
    );

    const selectedIds = participants
      .filter((p) => p.selected)
      .map((p) => p.newParticipantId)
      .filter(Boolean);
    const noDuplicates = selectedIds.length === new Set(selectedIds).size;

    return {
      valid: anyMatched && noSelectedUnmatched && noDuplicates,
      allManaged,
      anyMatched,
      noSelectedUnmatched,
      noDuplicates,
    };
  }, [participants]);

  const canSearch = currentDistributorId.trim() && newDistributorId.trim();

  const handleCurrentDistributorChange = (e) => {
    setCurrentDistributorId(e.target.value);
    if (sameDistributorId) setNewDistributorId(e.target.value);
  };

  const handleCurrentPlanSponsorChange = (e) => {
    setCurrentPlanSponsorId(e.target.value);
    if (samePlanSponsorId) setNewPlanSponsorId(e.target.value);
  };

  const handleSameDistributorChange = (e) => {
    setSameDistributorId(e.target.checked);
    if (e.target.checked) setNewDistributorId(currentDistributorId);
  };

  const handleSamePlanSponsorChange = (e) => {
    setSamePlanSponsorId(e.target.checked);
    if (e.target.checked) setNewPlanSponsorId(currentPlanSponsorId);
  };

  const handleFindManagedAccounts = async () => {
    setLoading(true);
    setParticipants([]);
    setFetched(false);

    try {
      const managedRes = await integratedTransitionApi.getManagedAccounts(
        currentDistributorId.trim(),
        currentPlanSponsorId.trim() || undefined
      );
      if (!managedRes.ok) {
        showToast({
          message: 'Failed to fetch managed accounts',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const managedAccounts = await managedRes.json();

      if (managedAccounts.length === 0) {
        showToast({
          message: 'No managed accounts found for this distributor',
          type: 'info',
        });
        setLoading(false);
        setFetched(true);
        return;
      }

      const findNewRes = await integratedTransitionApi.findNewParticipants({
        oldPlanSponsorId: currentPlanSponsorId.trim() || undefined,
        newPlanSponsorId: newPlanSponsorId.trim() || undefined,
        participantIds: managedAccounts,
      });

      if (!findNewRes.ok) {
        showToast({
          message: 'Failed to find new participants',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      const newParticipants = await findNewRes.json();

      const combined = newParticipants.map((item, index) => ({
        id: index,
        selected: item.matchingParticipants.length > 0,
        oldParticipantId: item.participant.participantId,
        firstName: item.participant.firstName,
        lastName: item.participant.lastName,
        last4: item.participant.last4,
        dateOfBirth: item.participant.dateOfBirth,
        oldStatus: item.participant.isManaged ? 'Managed' : 'Not Managed',
        newParticipantId:
          item.matchingParticipants.length === 1
            ? item.matchingParticipants[0].participantId
            : null,
        matchingParticipants: item.matchingParticipants,
      }));

      setParticipants(combined);
      setFetched(true);
      showToast({
        message: `Found ${combined.length} managed accounts`,
        type: 'success',
      });
    } catch (err) {
      showToast({ message: `Error: ${err.message}`, type: 'error' });
    }

    setLoading(false);
  };

  const handleSubmitTransition = async () => {
    setSubmitting(true);

    try {
      const res = await integratedTransitionApi.submitTransition([
        ...participants
          .filter((p) => p.selected && p.newParticipantId != null)
          .map((p) => ({
            oldParticipantId: p.oldParticipantId,
            newParticipantId: p.newParticipantId,
          })),
      ]);

      if (res.ok) {
        showToast({
          message: 'Transition submitted successfully',
          type: 'success',
        });
        setParticipants([]);
        setFetched(false);
      } else {
        const errorData = await res.json().catch(() => null);
        showToast({
          message: errorData?.message || 'Failed to submit transition',
          type: 'error',
        });
      }
    } catch (err) {
      showToast({ message: `Error: ${err.message}`, type: 'error' });
    }

    setSubmitting(false);
  };

  const handleSelectMatch = (id, newParticipantId) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === id ? { ...p, newParticipantId } : p))
    );
  };

  const handleToggleSelected = (id) => {
    setParticipants((prev) =>
      prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p))
    );
  };

  const handleToggleAll = () => {
    const eligible = participants.filter(
      (p) => p.matchingParticipants.length > 0
    );
    const allSelected = eligible.every((p) => p.selected);
    setParticipants((prev) =>
      prev.map((p) =>
        p.matchingParticipants.length === 0
          ? p
          : { ...p, selected: !allSelected }
      )
    );
  };

  const handleReset = () => {
    setParticipants([]);
    setFetched(false);
  };

  const handleClear = () => {
    setParticipants([]);
    setFetched(false);
    setCurrentDistributorId('');
    setCurrentPlanSponsorId('');
    setNewDistributorId('');
    setNewPlanSponsorId('');
    setSameDistributorId(false);
    setSamePlanSponsorId(false);
  };

  const formatDob = (dob) =>
    dob ? new Date(dob).toLocaleDateString('en-US') : '—';

  const eligible = participants.filter(
    (p) => p.matchingParticipants.length > 0
  );
  const allSelected = eligible.length > 0 && eligible.every((p) => p.selected);
  const someSelected = eligible.some((p) => p.selected);

  const SelectAllHeader = () => (
    <Checkbox
      size="small"
      checked={allSelected}
      indeterminate={someSelected && !allSelected}
      onChange={handleToggleAll}
    />
  );

  const columnDefs = [
    {
      headerComponent: SelectAllHeader,
      field: 'selected',
      sortable: false,
      suppressSizeToFit: true,
      width: 80,
      minWidth: 80,
      cellRenderer: (params) => (
        <Checkbox
          size="small"
          checked={!!params.value}
          disabled={params.data.matchingParticipants.length === 0}
          onChange={() => handleToggleSelected(params.data.id)}
        />
      ),
    },
    {
      headerName: 'Current (Old) Participant',
      children: [
        {
          headerName: 'Participant ID',
          field: 'oldParticipantId',
          sortable: true,
        },
        {
          headerName: 'Name',
          field: 'firstName',
          sortable: true,
          minWidth: 200,
          valueGetter: (params) =>
            `${params.data.firstName} ${params.data.lastName}`,
        },
        {
          headerName: 'Last 4',
          field: 'last4',
          sortable: true,
        },
        {
          headerName: 'DOB',
          field: 'dateOfBirth',
          sortable: true,
          valueFormatter: (params) => formatDob(params.value),
        },
        {
          headerName: 'Status',
          field: 'oldStatus',
          sortable: true,
          cellRenderer: (params) => (
            <Chip
              size="small"
              label={params.value}
              style={{
                backgroundColor: getStatusColor(params.value),
                color: '#fff',
              }}
            />
          ),
        },
      ],
    },

    {
      headerName: 'New Participant',
      children: [
        {
          colId: 'newParticipantId',
          headerName: 'Participant ID',
          sortable: false,
          valueGetter: (params) => {
            if (params.data.matchingParticipants.length === 0) return '—';
            const match = params.data.matchingParticipants[0] || {};
            return match.participantId || '—';
          },
        },
        {
          colId: 'newParticipantName',
          headerName: 'Name',
          sortable: false,
          minWidth: 200,
          valueGetter: (params) => {
            if (params.data.matchingParticipants.length === 0) return '—';
            const match = params.data.matchingParticipants[0] || {};
            return (
              [match.firstName, match.lastName].filter(Boolean).join(' ') || '—'
            );
          },
        },
        {
          colId: 'newParticipantLast4',
          headerName: 'Last 4',
          sortable: false,
          valueGetter: (params) => {
            if (params.data.matchingParticipants.length === 0) return '—';
            const match = params.data.matchingParticipants[0] || {};
            return match.last4 || '—';
          },
        },
        {
          colId: 'newParticipantDob',
          headerName: 'DOB',
          sortable: false,
          valueGetter: (params) => {
            if (params.data.matchingParticipants.length === 0) return '—';
            const match = params.data.matchingParticipants[0] || {};
            return formatDob(match.dateOfBirth);
          },
        },
      ],
    },

    {
      colId: 'matchStatus',
      headerName: 'Match Status',
      sortable: false,
      cellRenderer: (params) => {
        const row = params.data;
        if (row.matchingParticipants.length === 0)
          return (
            <Chip
              size="small"
              label="No Match"
              style={{ backgroundColor: '#f44336', color: '#fff' }}
            />
          );
        return (
          <Chip
            size="small"
            label={row.newParticipantId != null ? 'Matched' : 'Unselected'}
            style={{
              backgroundColor:
                row.newParticipantId != null ? '#4caf50' : '#ff9800',
              color: '#fff',
            }}
          />
        );
      },
    },
  ];

  const ValidationIndicator = ({ passed, label }) => (
    <li className={classes.validationItem}>
      <Chip
        size="small"
        label={passed ? '✓' : '✗'}
        style={{
          backgroundColor: passed ? '#4caf50' : '#f44336',
          color: '#fff',
          fontWeight: 'bold',
          minWidth: 28,
        }}
      />
      <Typography variant="body2">{label}</Typography>
    </li>
  );

  return (
    <div className={classes.wrapper}>
      <Paper elevation={1} className={classes.content}>
        <Typography variant="h6">
          Integrated To Integrated Transition Utility
        </Typography>

        <div className={classes.formGrid}>
          <div className={classes.fieldGroup}>
            <Typography variant="subtitle2" className={classes.fieldGroupLabel}>
              Current (Old) Distributor / Plan
            </Typography>
            <TextField
              label="Current Distributor ID"
              variant="outlined"
              size="small"
              value={currentDistributorId}
              onChange={handleCurrentDistributorChange}
              disabled={fetched}
            />
            <TextField
              label="Current Plan Sponsor ID"
              variant="outlined"
              size="small"
              value={currentPlanSponsorId}
              onChange={handleCurrentPlanSponsorChange}
              disabled={fetched}
            />
          </div>

          <div className={classes.fieldGroup}>
            <Typography variant="subtitle2" className={classes.fieldGroupLabel}>
              New Distributor / Plan
            </Typography>
            <TextField
              label="New Distributor ID"
              variant="outlined"
              size="small"
              value={newDistributorId}
              onChange={(e) => setNewDistributorId(e.target.value)}
              disabled={fetched || sameDistributorId}
            />
            <TextField
              label="New Plan Sponsor ID"
              variant="outlined"
              size="small"
              value={newPlanSponsorId}
              onChange={(e) => setNewPlanSponsorId(e.target.value)}
              disabled={fetched || samePlanSponsorId}
            />
          </div>
        </div>

        <Box display="flex" justifyContent="flex-end" gridGap="1rem" mt={3}>
          <LoaderSubmitButton
            variant="outlined"
            onClick={handleClear}
            disabled={loading || submitting}
          >
            Clear
          </LoaderSubmitButton>
          {fetched && (
            <LoaderSubmitButton
              variant="outlined"
              onClick={handleReset}
              disabled={loading || submitting}
            >
              Reset
            </LoaderSubmitButton>
          )}
          <LoaderSubmitButton
            icon={<SearchOutlined />}
            variant="contained"
            color="primary"
            disabled={!canSearch || loading || fetched}
            loading={loading}
            onClick={handleFindManagedAccounts}
          >
            Find Managed Accts
          </LoaderSubmitButton>
        </Box>
      </Paper>

      {participants.length > 0 && (
        <section className={classes.mainContent}>
          <Paper elevation={1} className={classes.content}>
            <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
              Participant Mapping ({participants.length} accounts)
            </Typography>
            <FCGrid columnDefs={columnDefs} rowData={participants} search />
          </Paper>
          <Paper elevation={1} className={classes.content}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Transition Validation
            </Typography>
            <ul className={classes.validationList}>
              <ValidationIndicator
                passed={validation.allManaged}
                label="All old participants are currently Managed"
              />
              <ValidationIndicator
                passed={validation.anyMatched}
                label="At least one participant has been matched"
              />
              <ValidationIndicator
                passed={validation.noSelectedUnmatched}
                label="All selected participants have a match chosen"
              />
              <ValidationIndicator
                passed={validation.noDuplicates}
                label="No new participant is selected more than once"
              />
            </ul>
            <Box display="flex" justifyContent="flex-end">
              <LoaderSubmitButton
                icon={<SendOutlined />}
                variant="contained"
                color="primary"
                disabled={!validation.valid || submitting}
                loading={submitting}
                onClick={handleSubmitTransition}
              >
                Submit for Transition
              </LoaderSubmitButton>
            </Box>
          </Paper>
        </section>
      )}

      {fetched && participants.length === 0 && (
        <Paper elevation={1} className={classes.content}>
          <Typography variant="body1" color="textSecondary" align="center">
            No managed accounts found for the specified distributor.
          </Typography>
        </Paper>
      )}
    </div>
  );
};
